/**
 * Application-wide constants
 */

// Storage keys (using "C2" prefix for Corgee v2)
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'C2_access_token',
  REFRESH_TOKEN: 'C2_refresh_token',
  USER_DATA: 'C2_userData',
  TENANT_DATA: 'C2_tenantData',
  TENANCY_NAME: 'C2_tenancyName',
  USERNAME: 'C2_username',
  GIVENNAME: 'C2_givenname',
  PERSONID: 'C2_personid',
  LAYERID: 'C2_layerid',
  IMAGEPROFILE: 'C2_imageprofile',
  GROUP_ID: 'C2_groupId',
  GROUP_NAME: 'C2_groupName',
  DEFAULT_GROUP_ID: 'C2_defaultGroupId',
  DEFAULT_GROUP_NAME: 'C2_defaultGroupName',
  N_GROUPS: 'C2_nGroups',
  THEME: 'C2_theme',
  DASHBOARD_CONFIG: 'C2_dashboardConfig',
  FILTER_CONFIG: 'C2_filterConfig',
  LAYERS: 'C2_layers',
  RECENT_DASHBOARDS: 'C2_recentDashboards',
  HIDDEN_LAYERS: 'C2_hiddenLayers',
  CURRENT_DASHBOARD: 'C2_currentDashboard',
  QUICK_FILTERS: 'C2_quickfilters',
  SIDEBAR_PROJECTS_OPEN: 'C2_projectListWasOpened',
  SIDEBAR_CARDTYPES_OPEN: 'C2_cardsTypeWasOpened',
  SIDEBAR_ORGANIZE_OPEN: 'C2_WasOpenedGTD',
  PERSONAL_FILTER_CONFIG: 'personalFilterConfig',
  IS_ENABLED_CUSTOM_BRANDING: 'C2_isEnabledCustomBranding',
  BRAND_NAME: 'C2_brandName',
  BRAND_IMAGE: 'C2_brandImage',
  BRAND_SHAPE: 'C2_brandShape',
} as const;

// Client-side body guard, not a server limit (IIS accepts up to 1 GB).
export const MAX_BODY_BYTES = 20000000;

export const BODY_TOO_LARGE_MESSAGE =
  `The content is too large to save (over ${Math.round(MAX_BODY_BYTES / 1000000)} MB). ` +
  'Pasted images are stored inside the body — remove or resize one and try again.';

// API endpoints
export const API_ENDPOINTS = {
  // Auth
  LOGIN: '/auth/login',
  LOGOUT: '/auth/logout',
  REFRESH: '/auth/refresh',
  REGISTER: '/auth/register',
  FORGOT_PASSWORD: '/auth/forgot-password',
  RESET_PASSWORD: '/auth/reset-password',
  CHANGE_PASSWORD: '/auth/change-password',

  // User
  USER: '/user',
  USER_PROFILE: '/user/profile',
  USER_SETTINGS: '/user/settings',

  // Dashboard
  DASHBOARDS: '/dashboards',
  DASHBOARD: (id: string) => `/dashboards/${id}`,
  DUPLICATE_DASHBOARD: (id: string) => `/dashboards/${id}/duplicate`,

  // Conversations
  CONVERSATIONS: '/conversations',
  CONVERSATION: (id: string) => `/conversations/${id}`,
  CONVERSATION_REPLIES: (id: string) => `/conversations/${id}/replies`,
  CONVERSATION_ATTACHMENTS: (id: string) => `/conversations/${id}/attachments`,

  // Projects
  PROJECTS: '/projects',
  PROJECT: (id: string) => `/projects/${id}`,
  PROJECT_PEOPLE: (id: string) => `/projects/${id}/people`,
  PROJECT_PHASES: (id: string) => `/projects/${id}/phases`,

  // People
  PEOPLE: '/people',
  PERSON: (id: string) => `/people/${id}`,
  APPROVE_PERSON: (id: string) => `/people/${id}/approve`,
  PERSON_AUTH: (id: string) => `/people/${id}/auth`,

  // Security
  SECURITY_GROUPS: '/security-groups',
  SECURITY_GROUP: (id: string) => `/security-groups/${id}`,
  USER_GROUPS: '/user-groups',
  USER_GROUP: (id: string) => `/user-groups/${id}`,

  // Time Tracking
  TIME_TRACKING: '/time-tracking',
  TIME_ENTRIES: '/time-entries',
  TIME_ENTRY: (id: string) => `/time-entries/${id}`,
  TIME_REPORTS: '/time-reports',

  // Reports
  REPORTS: '/reports',
  REPORT_PTO: '/reports/pto',
  REPORT_ATTENDANCE: '/reports/attendance',
  REPORT_EFFICIENCY: '/reports/efficiency',
  REPORT_PRODUCTIVITY: '/reports/productivity',
  REPORT_MONTHLY: '/reports/monthly-performance',
  REPORT_ETA_COST: '/reports/eta-cost',

  // Custom Fields
  CUSTOM_FIELDS: '/custom-fields',
  CUSTOM_FIELD: (id: string) => `/custom-fields/${id}`,
  CUSTOM_FIELD_VALUES: '/custom-field-values',

  // Files
  FILES: '/files',
  FILE: (id: string) => `/files/${id}`,
  FILE_UPLOAD: '/files/upload',

  // Notifications
  NOTIFICATIONS: '/notifications',
  MARK_NOTIFICATIONS_READ: '/notifications/mark-read',
} as const;

// Pagination defaults
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
  INITIAL_PAGE: 1,
} as const;

// Time constants
export const TIME = {
  DEBOUNCE_DELAY: 300,
  DEBOUNCE_LONG_DELAY: 500,
  THROTTLE_DELAY: 100,
  TOAST_DURATION: 3000,
  TOAST_LONG_DURATION: 5000,
  ANIMATION_DURATION: 300,
  SOCKET_RECONNECT_DELAY: 3000,
  SOCKET_RECONNECT_ATTEMPTS: 5,
} as const;

// File constraints
export const FILE_CONSTRAINTS = {
  MAX_FILE_SIZE: 50 * 1024 * 1024, // 50MB
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
  ALLOWED_DOCUMENT_TYPES: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ],
  ALLOWED_VIDEO_TYPES: ['video/mp4', 'video/webm', 'video/quicktime'],
} as const;

// Regex patterns
export const REGEX = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  URL: /^https?:\/\/[\w\-]+(\.[\w\-]+)+[/#?]?.*$/,
  SLUG: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
  PHONE: /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
} as const;

// Theme
export const THEME = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
} as const;

// Status
export const STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  ARCHIVED: 'archived',
} as const;

// Priority
export const PRIORITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  URGENT: 'urgent',
} as const;

// HTTP Status Codes
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
} as const;
